/* eslint-disable */
"use client";

import { Link } from "./router";
import { useRouter } from "./router";
import { useEffect, useMemo, useState } from "react";
import {
  BadgeCheck,
  Calendar,
  CalendarPlus,
  ChevronRight,
  CircleSlash,
  FileSpreadsheet,
  History,
  Loader2,
  Plus,
  Search,
  Sparkles,
  Trash2,
  UsersRound,
  Wallet,
  X,
} from "lucide-react";
import type { payrollPeriods } from "./types";
import { Button, Card, EmptyState, Field, Modal, Select, StatusPill, TextInput } from "./ui";
import { PageHeader } from "./sidebar";
import { useUrlFilters } from "./router";
import { generatePayrollAction, lockPayrollAction, savePayrollAttendanceOverridesAction, savePayrollLoanSkipsAction, savePayrollSalaryAdjustmentsAction } from "./api";
import {
  DEFAULT_PRESETS,
  activeGovColumnsForCutoff,
  holidayMultiplier,
  holidayTypeLabel,
  listWeekdays,
  resolveCutoffMode,
  resolvePreset,
  suggestPeriodName,
  type CutoffMode,
  type GovColumn,
  type HolidayInput,
  type PresetShape,
  GOV_COLUMNS,
} from "./payroll-shared";
import { cn, formatDate, formatPeso, toISODate } from "./utils";
import { RollingNumber } from "./rolling-number";

type PeriodRow = typeof payrollPeriods.$inferSelect;

export type PayrollBrand = {
  basePath: string;
  eyebrow: string;
  title: string;
  description: string;
};

type PayrollPreview = {
  name: string;
  startDate: string;
  endDate: string;
  status: string;
  workingDays: number;
  employeeCount: number;
  totalGross: string;
  totalDeductions: string;
  totalNet: string;
  ledger: string;
  rows: Array<{
    employee: any;
    record: any;
  }>;
};

type AttendanceDayStatus = "present" | "absent" | "halfday" | "non_working" | "holiday" | "saturday_rule";
type CalendarViewMode = "attendance" | "tardiness" | "undertime";

type AttendanceCalendarDay = {
  date: string;
  status: AttendanceDayStatus;
  reason?: string;
  tardinessMinutes?: number;
  undertimeMinutes?: number;
};

type AttendanceOverrideMap = Record<string, Record<string, "present" | "absent">>;
type SalaryAdjustmentUnit = "day" | "hour" | "minute" | "amount";
type SalaryAdjustmentDraft = {
  id: string;
  title: string;
  value: string;
  unit: SalaryAdjustmentUnit;
};
type SalaryAdjustmentMap = Record<string, Array<{ title: string; value: string; unit: SalaryAdjustmentUnit }>>;
type LoanSkipMap = Record<string, number[]>;
type CalendarModalState = {
  row: { employee: any; record: any };
  initialViewMode: CalendarViewMode;
};
type SalaryAdjustmentModalState = {
  row: { employee: any; record: any };
};
type LoanDetailsModalState = {
  employeeId: string | number;
};

const SALARY_ADJUSTMENT_UNITS: Array<{ value: SalaryAdjustmentUnit; label: string }> = [
  { value: "day", label: "Day" },
  { value: "hour", label: "Hour" },
  { value: "minute", label: "Minute" },
  { value: "amount", label: "Amount" },
];
const MAX_SALARY_ADJUSTMENT_VALUE = 1000000;
let salaryAdjustmentSeed = 0;

function toMoney(value: unknown) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function toPositiveMoney(value: unknown) {
  return Math.max(0, toMoney(value));
}

function parseIsoDate(value: string) {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, (month || 1) - 1, day || 1);
}

function toIsoLocal(date: Date) {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function monthLabel(value: string) {
  return parseIsoDate(`${value}-01`).toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

function buildCalendarMonths(startDate: string, endDate: string) {
  if (!startDate || !endDate || startDate > endDate) return [];
  const start = parseIsoDate(startDate);
  const end = parseIsoDate(endDate);
  const months: string[] = [];
  const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
  const limit = new Date(end.getFullYear(), end.getMonth(), 1);
  while (cursor <= limit) {
    months.push(`${cursor.getFullYear()}-${`${cursor.getMonth() + 1}`.padStart(2, "0")}`);
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return months;
}

function buildMonthCells(monthKey: string) {
  const monthStart = parseIsoDate(`${monthKey}-01`);
  const monthEnd = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0);
  const firstGrid = new Date(monthStart);
  firstGrid.setDate(monthStart.getDate() - monthStart.getDay());
  const lastGrid = new Date(monthEnd);
  lastGrid.setDate(monthEnd.getDate() + (6 - monthEnd.getDay()));
  const cells: string[] = [];
  const cursor = new Date(firstGrid);
  while (cursor <= lastGrid) {
    cells.push(toIsoLocal(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return cells;
}

function formatMinutesSummary(value: unknown) {
  const minutes = Math.max(0, Number(value || 0));
  return `${minutes} min`;
}

function formatSlashDate(value: string | null | undefined) {
  if (!value) return "--";
  const date = parseIsoDate(value);
  if (Number.isNaN(date.getTime())) return "--";
  return date.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
}

function loanPeriodTone(status?: string) {
  if (status === "Deducted") {
    return "bg-[color-mix(in_oklab,var(--success)_14%,transparent)] text-success";
  }
  if (status === "Skipped") {
    return "bg-[color-mix(in_oklab,var(--danger)_12%,transparent)] text-danger";
  }
  if (status === "Will Skip On Lock") {
    return "bg-[color-mix(in_oklab,var(--accent)_14%,transparent)] text-accent-strong";
  }
  return "bg-[color-mix(in_oklab,var(--accent)_14%,transparent)] text-accent-strong";
}

export function mergeLoanSkipSelections(current: LoanSkipMap, employeeId: string | number, skippedLoanIds: number[]) {
  const next = {
    ...current,
    [String(employeeId)]: skippedLoanIds,
  };
  if (!skippedLoanIds.length) {
    delete next[String(employeeId)];
  }
  return next;
}

export function hasQueuedLoanSkips(loanSkips: LoanSkipMap) {
  return Object.values(loanSkips).some((loanIds) => Array.isArray(loanIds) && loanIds.length > 0);
}

function calendarTone(status?: AttendanceDayStatus) {
  if (status === "holiday") return "bg-[var(--primary)] text-white";
  if (status === "present") return "bg-[var(--success)] text-white";
  if (status === "absent") return "bg-[var(--danger)] text-white";
  if (status === "halfday") return "bg-[color-mix(in_oklab,var(--accent)_78%,transparent)] text-[#1d1600]";
  if (status === "saturday_rule") return "bg-[color-mix(in_oklab,var(--accent)_72%,transparent)] text-[#1d1600]";
  if (status === "non_working") return "bg-surface3 text-muted";
  return "bg-surface text-faint";
}

export function formatPreviewAbsentDays(value: unknown) {
  const days = Math.max(0, Number(value || 0));
  if (!Number.isFinite(days)) return "0";
  return Number.isInteger(days) ? String(days) : days.toFixed(1).replace(/\.0$/, "");
}

function calendarMetricTone(viewMode: CalendarViewMode, minutes: number) {
  if (viewMode === "tardiness") {
    return minutes > 0
      ? "bg-[color-mix(in_oklab,var(--accent)_78%,transparent)] text-[#1d1600]"
      : "bg-[color-mix(in_oklab,var(--accent)_14%,transparent)] text-[color-mix(in_oklab,var(--accent)_72%,white)]";
  }
  if (viewMode === "undertime") {
    return minutes > 0
      ? "bg-[color-mix(in_oklab,var(--primary)_82%,transparent)] text-white"
      : "bg-[color-mix(in_oklab,var(--primary)_14%,transparent)] text-[color-mix(in_oklab,var(--primary)_72%,white)]";
  }
  return "";
}

function calendarMetricOutOfRangeTone() {
  return "border-border bg-surface3/70 text-muted opacity-45";
}

function calendarMetricValue(day: AttendanceCalendarDay | undefined, viewMode: CalendarViewMode) {
  if (!day) return 0;
  if (viewMode === "tardiness") return Math.max(0, Number(day.tardinessMinutes || 0));
  if (viewMode === "undertime") return Math.max(0, Number(day.undertimeMinutes || 0));
  return 0;
}

function resolveEffectiveCalendarDay(day?: AttendanceCalendarDay, overrideOn = false): AttendanceCalendarDay | undefined {
  if (!day) return undefined;
  if (!overrideOn || day.status === "non_working" || day.status === "holiday") return day;
  const nextStatus: AttendanceDayStatus = day.status === "present" ? "absent" : "present";
  return {
    ...day,
    status: nextStatus,
    reason: nextStatus,
  };
}

export function AbsenceCalendarModal({
  row,
  preview,
  open,
  initialViewMode = "attendance",
  onClose,
  onSave,
}: {
  row: { employee: any; record: any } | null;
  preview: PayrollPreview | null;
  open: boolean;
  initialViewMode?: CalendarViewMode;
  onClose: () => void;
  onSave: (employeeId: string | number, overrides: Record<string, "present" | "absent">) => Promise<string | null>;
}) {
  const calendarDays = useMemo(() => {
    const list = (row?.record?.attendanceCalendar || []) as AttendanceCalendarDay[];
    return new Map(list.map((day) => [day.date, day]));
  }, [row]);
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});
  const [saveError, setSaveError] = useState("");
  const [saving, setSaving] = useState(false);
  const [viewMode, setViewMode] = useState<CalendarViewMode>("attendance");
  const months = useMemo(
    () => buildCalendarMonths(preview?.startDate || "", preview?.endDate || ""),
    [preview?.endDate, preview?.startDate]
  );
  const dirty = useMemo(() => Object.values(overrides).some(Boolean), [overrides]);

  useEffect(() => {
    setOverrides({});
    setSaveError("");
    setSaving(false);
    setViewMode(initialViewMode);
  }, [initialViewMode, open, preview?.startDate, preview?.endDate, row?.employee?.id]);

  if (!open || !row || !preview) return null;

  const saveOverrides = async () => {
    if (!dirty || saving) return;
    const overridePayload = Object.entries(overrides).reduce<Record<string, "present" | "absent">>((acc, [dateKey, enabled]) => {
      if (!enabled) return acc;
      const baseInfo = calendarDays.get(dateKey);
      const info = resolveEffectiveCalendarDay(baseInfo, true);
      if (info?.status === "present" || info?.status === "absent") {
        acc[dateKey] = info.status;
      }
      return acc;
    }, {});
    setSaveError("");
    setSaving(true);
    const error = await onSave(row.employee.id, overridePayload);
    setSaving(false);
    if (error) {
      setSaveError(error);
      return;
    }
    onClose();
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Attendance calendar"
      subtitle={`${row.employee.firstName} ${row.employee.lastName} · ${formatDate(preview.startDate)} to ${formatDate(preview.endDate)}`}
      icon={<Calendar className="h-5 w-5" />}
      wide
      panelClassName="h-full max-w-none"
      contentClassName="min-h-0 p-0"
    >
      <div className="flex h-full min-h-0 flex-col gap-3 p-3 sm:gap-4 sm:p-4 lg:p-5">
        <div className="flex flex-wrap items-center gap-2">
          {[
            { key: "attendance", label: "Attendance" },
            { key: "tardiness", label: "Tardiness" },
            { key: "undertime", label: "Undertime" },
          ].map((option) => {
            const active = viewMode === option.key;
            return (
              <button
                key={option.key}
                type="button"
                aria-label={`Show ${option.label} view`}
                aria-pressed={active}
                data-state={active ? "active" : "inactive"}
                onClick={() => setViewMode(option.key as CalendarViewMode)}
                className={cn(
                  "inline-flex h-9 items-center justify-center rounded-xl border px-3.5 text-xs font-semibold transition-colors sm:text-sm",
                  active
                    ? "border-[color-mix(in_oklab,var(--primary)_65%,transparent)] bg-[color-mix(in_oklab,var(--primary)_18%,transparent)] text-text shadow-[0_10px_30px_-18px_var(--primary)]"
                    : "border-border bg-surface2 text-muted hover:border-border-strong hover:bg-surface3 hover:text-text"
                )}
              >
                {option.label}
              </button>
            );
          })}
        </div>

        <div className="flex flex-wrap items-center gap-2 text-xs font-semibold">
          {viewMode === "attendance" ? (
            <>
              <span className="rounded-full bg-[var(--primary)] px-3 py-1 text-white">Holiday</span>
              <span className="rounded-full bg-[var(--success)] px-3 py-1 text-white">Present</span>
              <span className="rounded-full bg-[color-mix(in_oklab,var(--accent)_78%,transparent)] px-3 py-1 text-[#1d1600]">Halfday</span>
              <span className="rounded-full bg-[var(--danger)] px-3 py-1 text-white">Absent</span>
              <span className="rounded-full bg-surface3 px-3 py-1 text-muted">Non-working</span>
            </>
          ) : (
            <span
              className={cn(
                "rounded-full px-3 py-1",
                viewMode === "tardiness"
                  ? "bg-[color-mix(in_oklab,var(--accent)_72%,transparent)] text-[#1d1600]"
                  : "bg-[color-mix(in_oklab,var(--primary)_72%,transparent)] text-white"
              )}
            >
              {viewMode === "tardiness" ? "Daily tardiness minutes" : "Daily undertime minutes"}
            </span>
          )}
        </div>

        <div
          className={cn(
            "grid min-h-0 flex-1 grid-cols-1 auto-rows-max content-start gap-3 overflow-auto lg:gap-4"
          )}
        >
          {months.map((month) => (
            <div key={month} className="flex min-h-[22rem] min-w-0 flex-col rounded-2xl border border-border bg-surface2/50 p-3 sm:p-4">
              <div className="mb-3 flex items-center justify-between gap-3">
                <h3 className="font-display text-base font-bold text-text sm:text-lg">{monthLabel(month)}</h3>
                <span className="shrink-0 text-[11px] font-semibold text-faint sm:text-xs">Sun - Sat</span>
              </div>

              <div className="mb-2 grid grid-cols-7 gap-1 text-center text-[10px] font-bold uppercase tracking-wide text-faint sm:gap-2 sm:text-[11px]">
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((label) => (
                  <div key={label} className="px-1 py-1 sm:px-2">{label}</div>
                ))}
              </div>

              <div className="grid flex-1 grid-cols-7 auto-rows-[minmax(4.75rem,1fr)] gap-1 sm:gap-2">
                {buildMonthCells(month).map((dateKey) => {
                  const baseInfo = calendarDays.get(dateKey);
                  const overrideOn = !!overrides[dateKey];
                  const info = resolveEffectiveCalendarDay(baseInfo, overrideOn);
                  const metricMinutes = calendarMetricValue(baseInfo, viewMode);
                  const outOfRange = !baseInfo;
                  const metricView = viewMode !== "attendance";
                  const metricOutOfRange = metricView && outOfRange;
                  const inMonth = dateKey.startsWith(month);
                  const toggleDisabled = metricView || !baseInfo || baseInfo.status === "non_working" || baseInfo.status === "holiday";
                  return (
                    <div
                      key={dateKey}
                      className={cn(
                        "relative flex h-full min-h-0 min-w-0 flex-col rounded-lg border border-border/70 px-1.5 py-1.5 text-[11px] sm:rounded-xl sm:px-2 sm:py-2 sm:text-xs lg:px-3 lg:py-2.5",
                        outOfRange
                          ? calendarMetricOutOfRangeTone()
                          : metricView
                            ? (metricOutOfRange ? calendarMetricOutOfRangeTone() : calendarMetricTone(viewMode, metricMinutes))
                            : calendarTone(info?.status),
                        !outOfRange && !metricView && !inMonth && "opacity-35"
                      )}
                    >
                      {viewMode === "attendance" && baseInfo && (
                        <button
                          type="button"
                          aria-label={`Toggle date override for ${dateKey}`}
                          aria-pressed={overrideOn}
                          data-state={overrideOn ? "on" : "off"}
                          disabled={toggleDisabled}
                          onClick={() => {
                            if (toggleDisabled) return;
                            setOverrides((current) => ({
                              ...current,
                              [dateKey]: !current[dateKey],
                            }));
                          }}
                          className={cn(
                            "absolute top-1 right-1 z-10 flex h-5 min-w-[2.25rem] cursor-pointer items-center justify-center rounded-full border px-1.5 text-[9px] font-bold uppercase tracking-wide transition-colors sm:top-2 sm:right-2",
                            overrideOn
                              ? "border-[color-mix(in_oklab,var(--accent)_60%,transparent)] bg-accent text-[#1d1600]"
                              : "border-border/80 bg-surface/80 text-faint",
                            toggleDisabled && "cursor-not-allowed opacity-45"
                          )}
                        >
                          {overrideOn ? "On" : "Off"}
                        </button>
                      )}
                      <span className="text-xs font-bold sm:text-sm lg:text-base">{Number(dateKey.slice(-2))}</span>
                      {viewMode === "attendance" ? (
                        info?.reason ? (
                          <span className="mt-1 line-clamp-2 text-[9px] font-semibold leading-tight uppercase tracking-wide sm:text-[10px] lg:text-[11px]">
                            {info.reason.replace("_", " ")}
                          </span>
                        ) : (
                          <span className="mt-auto" aria-hidden="true" />
                        )
                      ) : (
                        <>
                          <span className="mt-1 text-[9px] font-semibold uppercase tracking-wide opacity-80 sm:text-[10px] lg:text-[11px]">
                            {viewMode === "tardiness" ? "Tardiness" : "Undertime"}
                          </span>
                          {baseInfo ? (
                            <span
                              className="mt-auto text-[11px] font-bold tracking-wide sm:text-sm lg:text-base"
                              aria-label={`${viewMode === "tardiness" ? "Tardiness" : "Undertime"} minutes for ${dateKey}`}
                            >
                              {`${metricMinutes} min`}
                            </span>
                          ) : (
                            <span className="mt-auto" aria-hidden="true" />
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {saveError && (
          <p className="rounded-xl border border-[color-mix(in_oklab,var(--danger)_35%,transparent)] bg-[color-mix(in_oklab,var(--danger)_10%,transparent)] px-3.5 py-2.5 text-xs font-semibold text-danger">
            {saveError}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="accent" onClick={saveOverrides} disabled={viewMode !== "attendance" || !dirty || saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calendar className="h-4 w-4" />}
            Save
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export function SalaryAdjustmentModal({
  row,
  open,
  onClose,
  onSave,
}: {
  row: { employee: any; record: any } | null;
  open: boolean;
  onClose: () => void;
  onSave: (employeeId: string | number, rows: Array<{ title: string; value: string; unit: SalaryAdjustmentUnit }>) => Promise<string | null>;
}) {
  const [rows, setRows] = useState<SalaryAdjustmentDraft[]>([createSalaryAdjustmentDraft()]);
  const [saveError, setSaveError] = useState("");
  const [saving, setSaving] = useState(false);
  const [attemptedSave, setAttemptedSave] = useState(false);
  const [inputFeedback, setInputFeedback] = useState<Record<string, string>>({});

  useEffect(() => {
    setRows(parseSalaryAdjustmentDrafts(row?.record?.salaryAdjustments));
    setSaveError("");
    setSaving(false);
    setAttemptedSave(false);
    setInputFeedback({});
  }, [open, row?.employee?.id, row?.record?.salaryAdjustments]);

  if (!open || !row) return null;

  const rowErrors = rows.map((entry) => ({
    title: !entry.title.trim() ? "Title is required." : "",
    value: validateSalaryAdjustmentValue(entry.value),
  }));
  const hasValidationErrors = rowErrors.some((entry) => entry.title || entry.value);
  const adjustmentTotal = rows.reduce((sum, entry) => sum + computeSalaryAdjustmentAmount(entry, row.employee), 0);
  const baseGrossSalary = computePreviewGrossSalary(
    row.record?.halfMonthSalary,
    row.record?.absenceDeduction,
    row.record?.tardinessDeduction,
    row.record?.undertimeDeduction
  );
  const adjustedGrossSalary = baseGrossSalary + adjustmentTotal;

  const updateRow = (id: string, patch: Partial<SalaryAdjustmentDraft>) => {
    setRows((current) => current.map((entry) => (entry.id === id ? { ...entry, ...patch } : entry)));
  };

  const setRowFeedback = (id: string, message = "") => {
    setInputFeedback((current) => {
      if (!message && !current[id]) return current;
      const next = { ...current };
      if (message) next[id] = message;
      else delete next[id];
      return next;
    });
  };

  const addRow = () => {
    setRows((current) => [...current, createSalaryAdjustmentDraft()]);
  };

  const removeRow = (id: string) => {
    if (typeof window !== "undefined") {
      const confirmed = window.confirm("Delete this salary adjustment row?");
      if (!confirmed) return;
    }
    setRows((current) => {
      const next = current.filter((entry) => entry.id !== id);
      return next.length ? next : [createSalaryAdjustmentDraft()];
    });
    setRowFeedback(id);
  };

  const saveRows = async () => {
    setAttemptedSave(true);
    if (hasValidationErrors || saving) return;
    setSaving(true);
    setSaveError("");
    const error = await onSave(
      row.employee.id,
      rows.map((entry) => ({
        title: entry.title.trim(),
        value: entry.value,
        unit: entry.unit,
      }))
    );
    setSaving(false);
    if (error) {
      setSaveError(error);
      return;
    }
    onClose();
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Salary Adjustment"
      subtitle={`${row.employee.firstName} ${row.employee.lastName} · ${row.employee.employeeCode || "No code"}`}
      icon={<Wallet className="h-5 w-5" />}
      wide
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="rounded-xl border border-border bg-surface2/60 px-4 py-3">
            <p className="text-[11px] font-bold tracking-wide text-muted uppercase">Base Gross Salary</p>
            <p className="mt-1 text-lg font-bold text-text">{formatPeso(baseGrossSalary)}</p>
          </div>
          <div className="rounded-xl border border-border bg-surface2/60 px-4 py-3">
            <p className="text-[11px] font-bold tracking-wide text-muted uppercase">Adjustment Total</p>
            <p className="mt-1 text-lg font-bold text-accent-strong">{formatPeso(adjustmentTotal)}</p>
          </div>
          <div className="rounded-xl border border-border bg-surface2/60 px-4 py-3">
            <p className="text-[11px] font-bold tracking-wide text-muted uppercase">Total Adjusted Salary</p>
            <p className="mt-1 text-lg font-bold text-primary-strong">{formatPeso(adjustedGrossSalary)}</p>
          </div>
        </div>

        <div className="space-y-3">
          {rows.map((entry, index) => {
            const amount = computeSalaryAdjustmentAmount(entry, row.employee);
            const errors = rowErrors[index];
            const valueFeedback = inputFeedback[entry.id] || (attemptedSave ? errors.value : "");
            return (
              <div key={entry.id} className="rounded-2xl border border-border bg-surface2/50 p-3">
                <div className="mb-3 flex items-start justify-end">
                  <button
                    type="button"
                    onClick={() => removeRow(entry.id)}
                    aria-label={`Delete adjustment row ${index + 1}`}
                    title="Delete row"
                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-xl border border-border bg-surface2 text-faint transition-colors hover:border-danger hover:bg-[color-mix(in_oklab,var(--danger)_12%,transparent)] hover:text-danger"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                  <Field label="Adjustment Title">
                    <TextInput
                      value={entry.title}
                      onChange={(e) => updateRow(entry.id, { title: e.target.value })}
                      placeholder="e.g. Late release correction"
                      aria-label={`Adjustment title ${index + 1}`}
                    />
                    {attemptedSave && errors.title && <p className="mt-1 text-xs font-semibold text-danger">{errors.title}</p>}
                  </Field>
                  <Field label="Base Value">
                    <TextInput
                      value={entry.value}
                      onChange={(e) => {
                        const next = e.target.value;
                        if (!sanitizeNumericInput(next)) {
                          setRowFeedback(entry.id, "Use numbers only. Examples: -1, 0.5, or 12.75.");
                          return;
                        }
                        const valueError = validateSalaryAdjustmentValue(next, true);
                        if (valueError) {
                          setRowFeedback(entry.id, valueError);
                          return;
                        }
                        setRowFeedback(entry.id);
                        updateRow(entry.id, { value: next });
                      }}
                      inputMode="decimal"
                      placeholder="0.00"
                      aria-label={`Adjustment base value ${index + 1}`}
                      aria-invalid={!!valueFeedback}
                      pattern="-?[0-9]*(\\.[0-9]*)?"
                    />
                    {valueFeedback && <p className="mt-1 text-xs font-semibold text-danger">{valueFeedback}</p>}
                  </Field>
                  <Field label="Calculation Type" hint={formatPeso(amount)}>
                    <Select
                      value={entry.unit}
                      onChange={(e) => updateRow(entry.id, { unit: e.target.value as SalaryAdjustmentUnit })}
                      aria-label={`Adjustment type ${index + 1}`}
                    >
                      {SALARY_ADJUSTMENT_UNITS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </Select>
                    <p className="mt-2 text-xs font-semibold text-muted">
                      Calculated Amount: <span className="font-bold text-accent-strong">{formatPeso(amount)}</span>
                    </p>
                  </Field>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-between gap-3">
          <Button type="button" variant="outline" size="sm" onClick={addRow}>
            <Plus className="h-3.5 w-3.5" /> Add row
          </Button>
          <p className="text-xs text-muted">Choose Day, Hour, Minute, or direct Amount per row.</p>
        </div>

        {saveError && (
          <p className="rounded-xl border border-[color-mix(in_oklab,var(--danger)_35%,transparent)] bg-[color-mix(in_oklab,var(--danger)_10%,transparent)] px-3.5 py-2.5 text-xs font-semibold text-danger">
            {saveError}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="accent" onClick={saveRows} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wallet className="h-4 w-4" />}
            Save
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function LoanDetailsModal({
  row,
  open,
  onClose,
  pendingSkippedLoanIds,
  onQueueSkipCurrentPeriod,
}: {
  row: { employee: any; record: any } | null;
  open: boolean;
  onClose: () => void;
  pendingSkippedLoanIds: number[];
  onQueueSkipCurrentPeriod: (employeeId: string | number, loanIds: number[]) => void;
}) {
  if (!open || !row) return null;

  const loanDetails = Array.isArray(row.record?.loanDetails) ? row.record.loanDetails : [];
  const currentLoanPeriod = row.record?.currentLoanPeriod || {};
  const skippableLoanIds = Array.isArray(currentLoanPeriod?.skippableLoanIds)
    ? currentLoanPeriod.skippableLoanIds.map((value: unknown) => Number(value)).filter((value: number) => Number.isFinite(value) && value > 0)
    : [];
  const queuedSkipIds = new Set(pendingSkippedLoanIds);
  const skipQueuedForCurrentPeriod = skippableLoanIds.length > 0 && skippableLoanIds.every((loanId) => queuedSkipIds.has(loanId));

  const queueSkip = () => {
    if (!skippableLoanIds.length || skipQueuedForCurrentPeriod) return;
    onQueueSkipCurrentPeriod(row.employee.id, skippableLoanIds);
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Loan Details"
      subtitle={`${row.employee.firstName} ${row.employee.lastName} · ${row.employee.employeeCode || "No code"}`}
      icon={<History className="h-5 w-5" />}
      wide
    >
      <div className="space-y-4">
        {loanDetails.length ? (
          <div className="space-y-4">
            {loanDetails.map((loan: any) => {
              const currentStatus = queuedSkipIds.has(Number(loan.id)) ? "Will Skip On Lock" : loan.currentPeriodStatus;
              return (
              <div key={loan.id} className="rounded-2xl border border-border bg-surface2/50 p-4">
                <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-base font-bold text-text">{loan.loanType || "Loan record"}</p>
                    <p className="mt-1 text-xs text-muted">
                      {row.employee.firstName} {row.employee.lastName}
                    </p>
                  </div>
                  <span className={cn("rounded-full px-3 py-1 text-[11px] font-semibold", loanPeriodTone(currentStatus))}>
                    {currentStatus}
                  </span>
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  <div className="rounded-xl border border-border bg-surface px-3.5 py-3">
                    <p className="text-[11px] font-bold tracking-wide text-muted uppercase">Disbursement date</p>
                    <p className="mt-1 font-semibold text-text">{formatSlashDate(loan.disbursementDate)}</p>
                  </div>
                  <div className="rounded-xl border border-border bg-surface px-3.5 py-3">
                    <p className="text-[11px] font-bold tracking-wide text-muted uppercase">Repayment start</p>
                    <p className="mt-1 font-semibold text-text">{formatSlashDate(loan.startDate)}</p>
                  </div>
                  <div className="rounded-xl border border-border bg-surface px-3.5 py-3">
                    <p className="text-[11px] font-bold tracking-wide text-muted uppercase">Loan amount</p>
                    <p className="mt-1 font-semibold text-text">{formatPeso(loan.principal || 0)}</p>
                  </div>
                  <div className="rounded-xl border border-border bg-surface px-3.5 py-3">
                    <p className="text-[11px] font-bold tracking-wide text-muted uppercase">Repayment terms</p>
                    <p className="mt-1 font-semibold text-text">{loan.repaymentTerms || 0} installments</p>
                  </div>
                  <div className="rounded-xl border border-border bg-surface px-3.5 py-3">
                    <p className="text-[11px] font-bold tracking-wide text-muted uppercase">Interest rate</p>
                    <p className="mt-1 font-semibold text-text">{toMoney(loan.interestRate).toFixed(2)}%</p>
                  </div>
                  <div className="rounded-xl border border-border bg-surface px-3.5 py-3">
                    <p className="text-[11px] font-bold tracking-wide text-muted uppercase">Deduction per payout</p>
                    <p className="mt-1 font-semibold text-text">{formatPeso(loan.perPeriodDeduction || loan.deduction_per_payout || 0)}</p>
                  </div>
                  <div className="rounded-xl border border-border bg-surface px-3.5 py-3">
                    <p className="text-[11px] font-bold tracking-wide text-muted uppercase">Deduct to</p>
                    <p className="mt-1 font-semibold text-text">{loan.deductTo || loan.deduct_to || "Payroll"}</p>
                  </div>
                  <div className="rounded-xl border border-border bg-surface px-3.5 py-3">
                    <p className="text-[11px] font-bold tracking-wide text-muted uppercase">Remaining balance</p>
                    <p className="mt-1 font-semibold text-text">{formatPeso(loan.balance || 0)}</p>
                  </div>
                </div>

                <div className="mt-4 rounded-2xl border border-border bg-surface px-4 py-3">
                  <div className="mb-3 flex items-center gap-2">
                    <History className="h-4 w-4 text-primary-strong" />
                    <p className="text-sm font-bold text-text">Payroll period history</p>
                  </div>
                  <div className="space-y-2">
                    {(Array.isArray(loan.periodStatuses) ? loan.periodStatuses : []).length ? (
                      (loan.periodStatuses || []).map((period: any) => {
                        const periodStatus = period.isCurrent && queuedSkipIds.has(Number(loan.id)) ? "Will Skip On Lock" : period.status;
                        return (
                        <div
                          key={`${loan.id}-${period.periodId ?? period.startDate}`}
                          className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border bg-surface2/70 px-3.5 py-2.5"
                        >
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-text">{period.name || `${formatSlashDate(period.startDate)} - ${formatSlashDate(period.endDate)}`}</p>
                            <p className="text-xs text-muted">
                              {formatSlashDate(period.startDate)} - {formatSlashDate(period.endDate)}
                            </p>
                          </div>
                          <span className={cn("inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold", loanPeriodTone(periodStatus))}>
                            {periodStatus === "Deducted" ? <BadgeCheck className="h-3.5 w-3.5" /> : periodStatus === "Skipped" ? <CircleSlash className="h-3.5 w-3.5" /> : <History className="h-3.5 w-3.5" />}
                            {periodStatus}
                          </span>
                        </div>
                      );
                      })
                    ) : (
                      <p className="text-sm text-muted">No payroll periods are scheduled on or after this repayment start date yet.</p>
                    )}
                  </div>
                </div>
              </div>
            );
            })}
          </div>
        ) : (
          <EmptyState
            icon={<History className="h-6 w-6" />}
            title="No payroll loan deductions"
            description="This employee has no active payroll-targeted loan deductions in the current preview."
          />
        )}

        <div className="rounded-2xl border border-border bg-surface2/60 p-4">
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1.4fr_1fr_auto] lg:items-center">
            <div>
              <p className="text-[11px] font-bold tracking-wide text-muted uppercase">Current active payroll period</p>
              <p className="mt-1 text-base font-bold text-text">{currentLoanPeriod?.name || "Current preview period"}</p>
              <p className="mt-1 text-xs text-muted">
                {formatSlashDate(currentLoanPeriod?.startDate)} - {formatSlashDate(currentLoanPeriod?.endDate)}
              </p>
            </div>
            <div>
              <p className="text-[11px] font-bold tracking-wide text-muted uppercase">Scheduled deduction</p>
              <p className="mt-1 text-base font-bold text-accent-strong">{formatPeso(currentLoanPeriod?.scheduledAmount || 0)}</p>
              {skipQueuedForCurrentPeriod ? (
                <p className="mt-1 text-xs font-semibold text-accent-strong">Queued to skip when Lock Payroll is pressed.</p>
              ) : null}
            </div>
            <div className="flex items-center justify-start lg:justify-end">
              <Button variant="accent" onClick={queueSkip} disabled={!currentLoanPeriod?.canSkip || skipQueuedForCurrentPeriod}>
                <CircleSlash className="h-4 w-4" />
                {skipQueuedForCurrentPeriod ? "Queued" : "Skip"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export function computePreviewGrossSalary(
  halfMonthSalary: unknown,
  absence: unknown,
  tardiness: unknown,
  undertime: unknown,
  salaryAdjustmentTotal: unknown = 0
) {
  return (
    toPositiveMoney(halfMonthSalary)
    - toPositiveMoney(absence)
    - toPositiveMoney(tardiness)
    - toPositiveMoney(undertime)
    + toMoney(salaryAdjustmentTotal)
  );
}

export function computePreviewNetSalary(
  grossSalary: unknown,
  hdmf: unknown,
  philhealth: unknown,
  sss: unknown,
  withholdingTax: unknown,
  loan: unknown
) {
  return (
    toPositiveMoney(grossSalary)
    - toPositiveMoney(hdmf)
    - toPositiveMoney(philhealth)
    - toPositiveMoney(sss)
    - toPositiveMoney(withholdingTax)
    - toPositiveMoney(loan)
  );
}

function createSalaryAdjustmentDraft(partial?: Partial<SalaryAdjustmentDraft>): SalaryAdjustmentDraft {
  salaryAdjustmentSeed += 1;
  return {
    id: `salary-adjustment-${salaryAdjustmentSeed}`,
    title: "",
    value: "",
    unit: "amount",
    ...partial,
  };
}

function parseSalaryAdjustmentDrafts(entries: any): SalaryAdjustmentDraft[] {
  if (!Array.isArray(entries) || !entries.length) return [createSalaryAdjustmentDraft()];
  return entries.map((entry) =>
    createSalaryAdjustmentDraft({
      title: String(entry?.title || entry?.salaryAdjustment || "").trim(),
      value: String(entry?.value ?? entry?.daily ?? entry?.hourly ?? entry?.minute ?? entry?.amount ?? ""),
      unit: (String(entry?.unit || "amount").toLowerCase() as SalaryAdjustmentUnit),
    })
  );
}

function getEmployeeRate(employee: any, unit: SalaryAdjustmentUnit) {
  const dailyRate = toMoney(employee?.dailyRate);
  const hourlyRate = toMoney(employee?.hourlyRate) || (dailyRate > 0 ? dailyRate / 8 : 0);
  const minuteRate = toMoney(employee?.minutesRate) || (hourlyRate > 0 ? hourlyRate / 60 : 0);
  if (unit === "day") return dailyRate;
  if (unit === "hour") return hourlyRate;
  if (unit === "minute") return minuteRate;
  return 1;
}

export function computeSalaryAdjustmentAmount(draft: { value?: string | number; unit?: SalaryAdjustmentUnit }, employee: any) {
  const value = toMoney(draft?.value);
  const unit = draft?.unit || "amount";
  return value * getEmployeeRate(employee, unit);
}

function sanitizeNumericInput(value: string) {
  return /^-?\d*(?:\.\d*)?$/.test(value);
}

function validateSalaryAdjustmentValue(value: unknown, allowIncomplete = false) {
  const raw = String(value ?? "").trim();
  if (raw === "" || raw === "-" || raw === "." || raw === "-.") {
    return allowIncomplete ? "" : "Enter a valid number.";
  }
  const numeric = Number(raw);
  if (!Number.isFinite(numeric)) {
    return "Enter a valid number.";
  }
  if (Math.abs(numeric) > MAX_SALARY_ADJUSTMENT_VALUE) {
    return `Value must stay between -${MAX_SALARY_ADJUSTMENT_VALUE.toLocaleString()} and ${MAX_SALARY_ADJUSTMENT_VALUE.toLocaleString()}.`;
  }
  return "";
}

function getSalaryAdjustmentTotal(record: any, employee: any) {
  if (record?.salaryAdjustmentTotal !== undefined && record?.salaryAdjustmentTotal !== null && record?.salaryAdjustmentTotal !== "") {
    return toMoney(record.salaryAdjustmentTotal);
  }
  const entries = Array.isArray(record?.salaryAdjustments) ? record.salaryAdjustments : [];
  if (entries.length) {
    return entries.reduce((sum: number, entry: any) => sum + computeSalaryAdjustmentAmount(entry, employee), 0);
  }
  const amounts = Array.isArray(record?.amount) ? record.amount : [];
  return amounts.reduce((sum: number, amount: any) => sum + toMoney(amount), 0);
}

export function getPreviewAmounts(row: { employee: any; record: any }, cutoffMode: CutoffMode) {
  const monthlySalary =
    toPositiveMoney(row.employee?.monthlySalary) ||
    (toPositiveMoney(row.employee?.dailyRate) * (row.employee?.employeeType === "261" ? 261 : 313)) / 12;
  const halfMonthSalary = toPositiveMoney(row.record?.halfMonthSalary) || monthlySalary / 2;
  const absence = toPositiveMoney(row.record?.absenceDeduction);
  const tardiness = toPositiveMoney(row.record?.tardinessDeduction);
  const undertime = toPositiveMoney(row.record?.undertimeDeduction);
  const salaryAdjustmentTotal = getSalaryAdjustmentTotal(row.record, row.employee);
  const additionalAmount =
    row.employee?.govHdmf && cutoffMode !== "second_half"
      ? toPositiveMoney(row.record?.govHdmfNumber ?? row.employee?.govHdmfNumber)
      : 0;
  const hdmf =
    row.employee?.govHdmf && cutoffMode !== "second_half"
      ? (toPositiveMoney(row.record?.hdmfDeduction) || 200 + additionalAmount)
      : 0;
  const grossSalary = computePreviewGrossSalary(
    halfMonthSalary,
    absence,
    tardiness,
    undertime,
    salaryAdjustmentTotal
  );
  const loan = toPositiveMoney(row.record?.loanDeduction);
  const philhealth =
    row.employee?.govPhilhealth && cutoffMode !== "second_half"
      ? (toPositiveMoney(row.record?.philhealthDeduction) || (monthlySalary * 0.05) / 2)
      : 0;
  const sss = row.employee?.govSss && cutoffMode !== "first_half" ? toPositiveMoney(row.record?.sssDeduction) : 0;
  const withholdingTax =
    row.employee?.govTax && !row.employee?.taxExempt && cutoffMode !== "first_half"
      ? (toPositiveMoney(row.record?.withholdingTaxDeduction) || toPositiveMoney(row.record?.taxDeduction))
      : 0;
  const calculationIssues = Array.isArray(row.record?.calculationIssues) ? row.record.calculationIssues : [];
  const netPay = computePreviewNetSalary(grossSalary, hdmf, philhealth, sss, withholdingTax, loan);
  return {
    halfMonthSalary,
    absence,
    tardiness,
    undertime,
    grossSalary,
    salaryAdjustmentTotal,
    additionalAmount,
    loan,
    hdmf,
    philhealth,
    sss,
    withholdingTax,
    calculationIssues,
    hasCalculationIssue: Boolean(row.record?.hasCalculationIssue || calculationIssues.length),
    netPay,
  };
}

export function getPreviewGovColumnCellDisplay(
  row: { employee: any; record: any },
  cutoffMode: CutoffMode,
  columnKey: GovColumn["key"]
) {
  const amounts = getPreviewAmounts(row, cutoffMode);
  if (columnKey === "hdmf") {
    return {
      text: formatPeso(amounts.hdmf),
      detailText: `Additional ${formatPeso(amounts.additionalAmount)}`,
      isPlaceholder: false,
      isIdentifier: true,
    };
  }

  return {
    text: formatPeso(amounts[columnKey]),
    detailText: "",
    isPlaceholder: false,
    isIdentifier: false,
  };
}

export function formatPreviewNetColumnValue(value: number) {
  return formatPeso(value < 0 ? 0 : value);
}

export const PAYROLL_BRAND: PayrollBrand = {
  basePath: "/payroll",
  eyebrow: "Disbursements",
  title: "Payroll periods",
  description:
    "Generate payroll runs with holiday rules. Finalized runs are locked and secured by an admin PIN.",
};

/* ------------------------------ generate modal ------------------------------ */
function GenerateModal({
  activeEmployees,
  presets,
  basePath,
  onClose,
}: {
  activeEmployees: number;
  presets: PresetShape[];
  basePath: string;
  onClose: () => void;
}) {
  const router = useRouter();
  const today = new Date();
  const [name, setName] = useState("");
  const [nameTouched, setNameTouched] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [holidayRows, setHolidayRows] = useState<HolidayInput[]>([]);
  const [preview, setPreview] = useState<PayrollPreview | null>(null);
  const [calendarModal, setCalendarModal] = useState<CalendarModalState | null>(null);
  const [salaryAdjustmentModal, setSalaryAdjustmentModal] = useState<SalaryAdjustmentModalState | null>(null);
  const [loanDetailsModal, setLoanDetailsModal] = useState<LoanDetailsModalState | null>(null);
  const [attendanceOverrides, setAttendanceOverrides] = useState<AttendanceOverrideMap>({});
  const [salaryAdjustments, setSalaryAdjustments] = useState<SalaryAdjustmentMap>({});
  const [loanSkips, setLoanSkips] = useState<LoanSkipMap>({});
  const [error, setError] = useState("");
  const [pendingAction, setPendingAction] = useState<"generate" | "lock" | null>(null);
  const busy = pendingAction !== null;
  const ledger = basePath === "/mobility-fund" ? "mobility" : "payroll";
  const queuedLoanSkips = useMemo(() => hasQueuedLoanSkips(loanSkips), [loanSkips]);
  const loanDetailsRow = useMemo(
    () => preview?.rows?.find((row) => String(row.employee?.id) === String(loanDetailsModal?.employeeId || "")) || null,
    [loanDetailsModal?.employeeId, preview?.rows]
  );

  const weekdays = useMemo(() => listWeekdays(startDate, endDate), [startDate, endDate]);
  const isSemiMonthly = weekdays.length > 0 && weekdays.length < 20;
  const cutoffMode = useMemo(
    () => resolveCutoffMode(preview?.startDate || startDate, preview?.endDate || endDate),
    [preview?.startDate, preview?.endDate, startDate, endDate]
  );
  const activeGovColumns = useMemo(() => activeGovColumnsForCutoff(cutoffMode), [cutoffMode]);
  const previewSummary = useMemo(() => {
    const rows = preview?.rows || [];
    return rows.reduce(
      (acc, row) => {
        const amounts = getPreviewAmounts(row, cutoffMode);
        acc.halfMonthSalary += amounts.halfMonthSalary;
        acc.grossSalary += amounts.grossSalary;
        acc.govDeductions += activeGovColumns.reduce((sum, col) => sum + amounts[col.key], 0);
        acc.loan += amounts.loan;
        acc.netPay += amounts.netPay;
        return acc;
      },
      { halfMonthSalary: 0, grossSalary: 0, govDeductions: 0, loan: 0, netPay: 0 }
    );
  }, [activeGovColumns, cutoffMode, preview?.rows]);
  const resetPreview = () => {
    setPreview(null);
    setCalendarModal(null);
    setSalaryAdjustmentModal(null);
    setLoanDetailsModal(null);
    setAttendanceOverrides({});
    setSalaryAdjustments({});
    setLoanSkips({});
    setError("");
  };
  const openCalendarModal = (row: { employee: any; record: any }, initialViewMode: CalendarViewMode = "attendance") => {
    setCalendarModal({ row, initialViewMode });
  };
  const openSalaryAdjustmentModal = (row: { employee: any; record: any }) => {
    setSalaryAdjustmentModal({ row });
  };
  const openLoanDetailsModal = (row: { employee: any; record: any }) => {
    setLoanDetailsModal({ employeeId: row.employee.id });
  };
  const buildPayload = () => ({
    name,
    startDate,
    endDate,
    holidays: holidayRows.filter((h) => h.name.trim()),
    ledger,
    attendanceOverrides,
    salaryAdjustments,
    loanSkips,
  });

  const applyDates = (s: string, e: string) => {
    setStartDate(s);
    setEndDate(e);
    if (!nameTouched) setName(suggestPeriodName(s, e));
    resetPreview();
  };

  const applyPreset = (p: PresetShape) => {
    const resolved = resolvePreset(p, today);
    if (resolved) applyDates(resolved.start, resolved.end);
  };

  const addHoliday = () =>
    {
      resetPreview();
      setHolidayRows((rows) => [
        ...rows,
        { date: startDate || toISODate(today), name: "", type: "regular", worked: false },
      ]);
    };
  const updateHoliday = (i: number, patch: Partial<HolidayInput>) =>
    {
      resetPreview();
      setHolidayRows((rows) => rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
    };
  const removeHoliday = (i: number) => {
    resetPreview();
    setHolidayRows((rows) => rows.filter((_, idx) => idx !== i));
  };

  const submit = async () => {
    setError("");
    setPreview(null);
    setPendingAction("generate");
    const res = await generatePayrollAction(buildPayload());
    setPendingAction(null);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setPreview(res.data?.preview || null);
    setAttendanceOverrides(extractAttendanceOverrides(res.data?.preview || null));
    setSalaryAdjustments(extractSalaryAdjustments(res.data?.preview || null));
    setLoanSkips(extractLoanSkips(res.data?.preview || null));
  };

  const lockPayroll = async () => {
    setError("");
    setPendingAction("lock");
    if (queuedLoanSkips) {
      const skipRes = await savePayrollLoanSkipsAction(buildPayload());
      if (!skipRes.ok) {
        setError(skipRes.error || "Unable to queue skipped loan deductions for this payroll period.");
        setPendingAction(null);
        return;
      }
    }
    const res = await lockPayrollAction(buildPayload());
    setPendingAction(null);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    onClose();
    router.push(`${basePath}/${res.data!.periodId}`);
    router.refresh();
  };

  const saveCalendarOverrides = async (employeeId: string | number, overridesForEmployee: Record<string, "present" | "absent">) => {
    const nextOverrides = {
      ...attendanceOverrides,
      [String(employeeId)]: overridesForEmployee,
    };
    if (!Object.keys(overridesForEmployee).length) {
      delete nextOverrides[String(employeeId)];
    }
    const res = await savePayrollAttendanceOverridesAction({
      ...buildPayload(),
      attendanceOverrides: nextOverrides,
    });
    if (!res.ok) return res.error || "Unable to save attendance edits.";
    setPreview(res.data?.preview || null);
    setAttendanceOverrides(extractAttendanceOverrides(res.data?.preview || null));
    setSalaryAdjustments(extractSalaryAdjustments(res.data?.preview || null));
    setLoanSkips(extractLoanSkips(res.data?.preview || null));
    return null;
  };

  const saveSalaryAdjustments = async (
    employeeId: string | number,
    adjustmentsForEmployee: Array<{ title: string; value: string; unit: SalaryAdjustmentUnit }>
  ) => {
    const nextAdjustments = {
      ...salaryAdjustments,
      [String(employeeId)]: adjustmentsForEmployee,
    };
    if (!adjustmentsForEmployee.length) {
      delete nextAdjustments[String(employeeId)];
    }
    const res = await savePayrollSalaryAdjustmentsAction({
      ...buildPayload(),
      salaryAdjustments: nextAdjustments,
    });
    if (!res.ok) return res.error || "Unable to save salary adjustments.";
    setPreview(res.data?.preview || null);
    setAttendanceOverrides(extractAttendanceOverrides(res.data?.preview || null));
    setSalaryAdjustments(extractSalaryAdjustments(res.data?.preview || null));
    setLoanSkips(extractLoanSkips(res.data?.preview || null));
    return null;
  };

  const saveLoanSkips = (employeeId: string | number, skippedLoanIds: number[]) => {
    setLoanSkips((current) => mergeLoanSkipSelections(current, employeeId, skippedLoanIds));
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Generate payroll"
      subtitle="Computes attendance pay, holiday premiums, SSS & tax brackets, and loan deductions for every active employee."
      icon={<Wallet className="h-5 w-5" />}
      wide
      fullscreen
      hideBackdrop
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Period name" className="sm:col-span-2">
          <TextInput
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setNameTouched(true);
              resetPreview();
            }}
            placeholder="e.g. Jan 1 \u2013 15, 2026"
            autoFocus
          />
        </Field>
        <Field label="Start date">
          <TextInput type="date" value={startDate} onChange={(e) => applyDates(e.target.value, endDate || e.target.value)} />
        </Field>
        <Field label="End date">
          <TextInput type="date" value={endDate} onChange={(e) => applyDates(startDate, e.target.value)} min={startDate} />
        </Field>
      </div>

      <div className="mt-3 rounded-xl border border-border bg-surface2/40 p-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] font-bold tracking-wide text-faint uppercase">Quick range:</span>
          {/* Primary ranges first */}
          {["First Half", "Second Half"].map((label) => {
            const p = presets.find((x) => x.label === label) || DEFAULT_PRESETS.find((x) => x.label === label);
            if (!p) return null;
            const resolved = resolvePreset(p, today);
            const selected = !!resolved && resolved.start === startDate && resolved.end === endDate;
            return (
              <button
                key={label}
                type="button"
                onClick={() => applyPreset(p)}
                disabled={!resolved}
                title={resolved ? `${formatDate(resolved.start)} \u2014 ${formatDate(resolved.end)}` : "Incomplete preset"}
                className={cn(
                  "cursor-pointer rounded-full border px-4 py-1.5 text-[11px] font-bold transition-all disabled:pointer-events-none disabled:opacity-40",
                  selected
                    ? "border-accent bg-[color-mix(in_oklab,var(--accent)_16%,transparent)] text-accent-strong shadow-[0_0_15px_-5px_var(--accent)]"
                    : "bg-surface2 text-muted hover:border-primary hover:text-primary-strong"
                )}
              >
                {label}
              </button>
            );
          })}

          {/* Separator if there are other presets */}
          {presets.filter((p) => p.label !== "First Half" && p.label !== "Second Half").length > 0 && (
            <div className="mx-1 h-4 w-px bg-border/60" />
          )}

          {presets
            .filter((p) => p.label !== "First Half" && p.label !== "Second Half")
            .map((p, i) => {
              const resolved = resolvePreset(p, today);
              const selected = !!resolved && resolved.start === startDate && resolved.end === endDate;
              return (
                <button
                  key={`${p.label}-${i}`}
                  type="button"
                  onClick={() => applyPreset(p)}
                  disabled={!resolved}
                  title={resolved ? `${formatDate(resolved.start)} \u2014 ${formatDate(resolved.end)}` : "Incomplete preset"}
                  className={cn(
                    "cursor-pointer rounded-full border px-3 py-1.5 text-[11px] font-bold transition-all disabled:pointer-events-none disabled:opacity-40",
                    selected
                      ? "border-accent bg-[color-mix(in_oklab,var(--accent)_16%,transparent)] text-accent-strong"
                      : "bg-surface2 text-muted hover:border-primary hover:text-primary-strong"
                  )}
                >
                  {p.label}
                </button>
              );
            })}
          <Link
            href="/preferences"
            className="rounded-full border px-3 py-1.5 text-[11px] font-bold text-faint transition-all hover:border-primary hover:text-primary-strong"
            title="Customize quick ranges in Preferences"
          >
            Edit ranges
          </Link>
          {weekdays.length > 0 && (
            <span className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-[color-mix(in_oklab,var(--accent)_14%,transparent)] px-3 py-1.5 text-[11px] font-bold text-accent-strong">
              <Sparkles className="h-3.5 w-3.5" />
              {weekdays.length} working day{weekdays.length === 1 ? "" : "s"} &#183; {isSemiMonthly ? "semi-monthly (\u00bd deductions)" : "monthly (full deductions)"}
            </span>
          )}
        </div>
      </div>

      {/* holidays */}
      <div className="mt-5">
        <div className="mb-2.5 flex items-center justify-between">
          <div>
            <p className="text-xs font-bold tracking-wide text-muted uppercase">Holidays in this period</p>
            <p className="mt-0.5 text-[11px] text-faint">Regular 100% / 200% if worked &#183; Special 0% / 130% if worked</p>
          </div>
          <Button variant="outline" size="sm" onClick={addHoliday}>
            <CalendarPlus className="h-3.5 w-3.5" /> Add holiday
          </Button>
        </div>

        {holidayRows.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border px-4 py-5 text-center text-xs text-faint">
            No holidays &mdash; every weekday is paid as a regular working day.
          </div>
        ) : (
          <div className="space-y-2">
            {holidayRows.map((h, i) => {
              const mult = holidayMultiplier(h.type, h.worked);
              return (
                <div key={i} className="grid grid-cols-[130px_1fr] items-center gap-2 rounded-xl bg-surface2 p-2.5 sm:grid-cols-[150px_1fr_auto_auto_auto]">
                  <TextInput
                    type="date"
                    value={h.date}
                    min={startDate}
                    max={endDate}
                    onChange={(e) => updateHoliday(i, { date: e.target.value })}
                    className="h-9 px-2.5 text-xs"
                  />
                  <TextInput
                    value={h.name}
                    onChange={(e) => updateHoliday(i, { name: e.target.value })}
                    placeholder="Holiday name (e.g. Independence Day)"
                    className="h-9 px-2.5 text-xs"
                  />
                  <div className="col-span-2 flex items-center gap-2 sm:col-span-1">
                    <Select
                      value={h.type}
                      onChange={(e) => updateHoliday(i, { type: e.target.value as "regular" | "special" })}
                      className="h-9 flex-1 px-2.5 text-xs"
                    >
                      <option value="regular">Regular</option>
                      <option value="special">Special</option>
                    </Select>
                    <label className="flex cursor-pointer items-center gap-1.5 text-[11px] font-semibold text-muted">
                      <input
                        type="checkbox"
                        checked={h.worked}
                        onChange={(e) => updateHoliday(i, { worked: e.target.checked })}
                        className="h-3.5 w-3.5 cursor-pointer accent-[var(--accent)]"
                      />
                      Worked
                    </label>
                  </div>
                  <span
                    className={cn(
                      "rounded-lg px-2 py-1 text-center text-[10px] font-bold whitespace-nowrap",
                      mult === 0
                        ? "bg-surface3 text-faint"
                        : mult >= 2
                          ? "bg-[color-mix(in_oklab,var(--accent)_18%,transparent)] text-accent-strong"
                          : "bg-[color-mix(in_oklab,var(--primary)_16%,transparent)] text-primary-strong"
                    )}
                    title={holidayTypeLabel(h.type, h.worked)}
                  >
                    &times;{mult.toFixed(1)}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeHoliday(i)}
                    className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg text-faint transition-colors hover:bg-surface3 hover:text-danger"
                    title="Remove"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="mt-5 flex items-center gap-3 rounded-xl border border-border bg-surface2 px-4 py-3">
        <UsersRound className="h-4 w-4 shrink-0 text-primary-strong" />
        <p className="text-xs leading-relaxed text-muted">
          Payroll will run for <span className="font-bold text-text">{activeEmployees} active employee{activeEmployees === 1 ? "" : "s"}</span>.
          Period-specific government and loan deductions are applied automatically.
        </p>
      </div>

      {preview && (
        <div className="mt-5">
          <div className="mb-2.5 flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-xs font-bold tracking-wide text-muted uppercase">Generated employee preview</p>
              <p className="mt-0.5 text-[11px] text-faint">
                Review the generated entries below, then lock the period to save it.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2 text-[11px] font-semibold">
              <span className="rounded-full bg-[color-mix(in_oklab,var(--primary)_14%,transparent)] px-3 py-1 text-primary-strong">
                Half Month Salary {formatPeso(previewSummary.halfMonthSalary)}
              </span>
              <span className="rounded-full bg-[color-mix(in_oklab,var(--accent)_14%,transparent)] px-3 py-1 text-accent-strong">
                Gross Salary {formatPeso(previewSummary.grossSalary)}
              </span>
              <span className="rounded-full bg-[color-mix(in_oklab,var(--danger)_12%,transparent)] px-3 py-1 text-danger">
                Gov. Deductions {formatPeso(previewSummary.govDeductions)}
              </span>
              <span className="rounded-full bg-[color-mix(in_oklab,var(--danger)_10%,transparent)] px-3 py-1 text-danger">
                Loan {formatPeso(previewSummary.loan)}
              </span>
              <span className="rounded-full bg-[color-mix(in_oklab,var(--accent)_14%,transparent)] px-3 py-1 text-accent-strong">
                Net {formatPeso(previewSummary.netPay)}
              </span>
            </div>
          </div>

          <div className="overflow-hidden rounded-xl border border-border bg-surface2/50">
            <div className="max-h-[min(42vh,28rem)] overflow-x-auto overflow-y-auto">
              <table className="min-w-[1380px] table-fixed border-collapse text-sm">
                <thead className="text-[11px] font-bold tracking-wide uppercase">
                  <tr className="border-b border-border/70 text-faint">
                    <th rowSpan={2} className="sticky top-0 z-20 w-[250px] border-b border-border/70 bg-surface px-4 py-3 text-left align-middle">Employee</th>
                    <th rowSpan={2} className="sticky top-0 z-20 w-[140px] border-b border-border/70 bg-surface px-3 py-3 text-right align-middle text-primary-strong">Half Month Salary</th>
                    <th colSpan={3} className="sticky top-0 z-20 border-b border-border/70 bg-surface px-3 py-2 text-center text-accent">Income Breakdown</th>
                    <th rowSpan={2} className="sticky top-0 z-20 w-[140px] border-b border-border/70 bg-surface px-3 py-3 text-right align-middle text-accent-strong">Gross Salary</th>
                    <th colSpan={activeGovColumns.length} className="sticky top-0 z-20 border-b border-border/70 bg-surface px-3 py-2 text-center text-danger">Gov. Deductions</th>
                    <th rowSpan={2} className="sticky top-0 z-20 w-[120px] border-b border-border/70 bg-surface px-3 py-3 text-right align-middle text-danger">Loan</th>
                    <th rowSpan={2} className="sticky top-0 z-20 w-[140px] border-b border-border/70 bg-surface px-4 py-3 text-right align-middle">Net</th>
                  </tr>
                  <tr className="border-b border-border/70 text-faint">
                    <th className="sticky top-[41px] z-20 w-[110px] border-b border-border/70 bg-surface px-3 py-2 text-right">Absence</th>
                    <th className="sticky top-[41px] z-20 w-[110px] border-b border-border/70 bg-surface px-3 py-2 text-right">Tardiness</th>
                    <th className="sticky top-[41px] z-20 w-[110px] border-b border-border/70 bg-surface px-3 py-2 text-right">Undertime</th>
                    {activeGovColumns.map((col) => (
                      <th key={col.key} className="sticky top-[41px] z-20 w-[120px] border-b border-border/70 bg-surface px-3 py-2 text-right">
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((row) => {
                    const amounts = getPreviewAmounts(row, cutoffMode);
                    return (
                      <tr key={row.employee.id} className="border-b border-border/60 last:border-b-0">
                        <td className="min-w-0 px-4 py-3">
                          <p className="truncate font-semibold text-text">
                            {row.employee.firstName} {row.employee.lastName}
                          </p>
                          <p className="truncate text-xs text-faint">
                            {row.employee.employeeCode || "No code"}{row.employee.position ? ` · ${row.employee.position}` : ""}
                          </p>
                          {amounts.hasCalculationIssue ? (
                            <p className="mt-1 truncate text-[11px] font-semibold text-accent">
                              Incomplete record: {amounts.calculationIssues[0] || "Review payroll inputs."}
                            </p>
                          ) : null}
                        </td>
                        <td className="px-3 py-3 text-right font-semibold text-primary-strong">{formatPeso(amounts.halfMonthSalary)}</td>
                        <td className="px-3 py-3 text-right">
                          <button
                            type="button"
                            className="ml-auto flex cursor-pointer flex-col items-end rounded-lg px-2 py-1 text-right transition-colors hover:bg-surface/80"
                            onClick={() => openCalendarModal(row, "attendance")}
                          >
                            <span className="text-xs font-bold text-danger">
                              {formatPreviewAbsentDays(row.record?.absentDays)} absence{Number(row.record?.absentDays || 0) === 1 ? "" : "s"}
                            </span>
                            <span className="text-muted">{formatPeso(amounts.absence)}</span>
                          </button>
                        </td>
                        <td className="px-3 py-3 text-right">
                          <button
                            type="button"
                            aria-label={`Open tardiness calendar for ${row.employee.firstName} ${row.employee.lastName}`}
                            className="ml-auto flex cursor-pointer flex-col items-end rounded-lg px-2 py-1 text-right transition-colors hover:bg-[color-mix(in_oklab,var(--accent)_10%,transparent)]"
                            onClick={() => openCalendarModal(row, "tardiness")}
                          >
                            <span className="text-xs font-bold text-accent-strong sm:text-[13px]">
                              {formatMinutesSummary(row.record?.tardinessMinutes)}
                            </span>
                            <span className="text-muted">{formatPeso(amounts.tardiness)}</span>
                          </button>
                        </td>
                        <td className="px-3 py-3 text-right">
                          <button
                            type="button"
                            aria-label={`Open undertime calendar for ${row.employee.firstName} ${row.employee.lastName}`}
                            className="ml-auto flex cursor-pointer flex-col items-end rounded-lg px-2 py-1 text-right transition-colors hover:bg-[color-mix(in_oklab,var(--primary)_10%,transparent)]"
                            onClick={() => openCalendarModal(row, "undertime")}
                          >
                            <span className="text-xs font-bold text-primary-strong transition-colors sm:text-[13px]">
                              {formatMinutesSummary(row.record?.undertimeMinutes)}
                            </span>
                            <span className="text-muted">{formatPeso(amounts.undertime)}</span>
                          </button>
                        </td>
                        <td className="px-3 py-3 text-right">
                          <button
                            type="button"
                            aria-label={`Open salary adjustment modal for ${row.employee.firstName} ${row.employee.lastName}`}
                            className="ml-auto flex cursor-pointer flex-col items-end rounded-lg px-2 py-1 text-right transition-colors hover:bg-[color-mix(in_oklab,var(--accent)_10%,transparent)]"
                            onClick={() => openSalaryAdjustmentModal(row)}
                          >
                            <span className="font-semibold text-accent-strong">{formatPeso(amounts.grossSalary)}</span>
                            <span className="text-xs font-semibold text-muted">
                              {amounts.salaryAdjustmentTotal !== 0 ? `Total Adj. ${formatPeso(amounts.salaryAdjustmentTotal)}` : "Adjust salary"}
                            </span>
                          </button>
                        </td>
                        {activeGovColumns.map((col) => (
                          <td key={col.key} className={`px-3 py-3 text-right ${col.key === "hdmf" ? "text-text" : "text-danger"}`}>
                            {(() => {
                              const display = getPreviewGovColumnCellDisplay(row, cutoffMode, col.key);
                              return (
                                <span className="ml-auto flex flex-col items-end">
                                  <span className={display.isIdentifier ? "font-semibold text-danger" : undefined}>{display.text}</span>
                                  {display.detailText ? (
                                    <span className={display.isPlaceholder ? "text-xs font-semibold text-faint" : "text-xs font-semibold text-primary-strong"}>
                                      {display.detailText}
                                    </span>
                                  ) : null}
                                </span>
                              );
                            })()}
                          </td>
                        ))}
                        <td className="px-3 py-3 text-right">
                          <button
                            type="button"
                            aria-label={`Open loan details for ${row.employee.firstName} ${row.employee.lastName}`}
                            className="ml-auto flex cursor-pointer flex-col items-end rounded-lg px-2 py-1 text-right text-danger transition-colors hover:bg-[color-mix(in_oklab,var(--danger)_10%,transparent)]"
                            onClick={() => openLoanDetailsModal(row)}
                          >
                            <span className="font-semibold">{formatPeso(amounts.loan)}</span>
                            <span className="text-xs font-semibold text-faint">
                              {Array.isArray(row.record?.loanDetails) && row.record.loanDetails.length ? "View loan details" : "No payroll loans"}
                            </span>
                          </button>
                        </td>
                        <td className="px-4 py-3 text-right font-bold text-accent-strong">{formatPreviewNetColumnValue(amounts.netPay)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-3 flex justify-end">
            <Button variant="accent" onClick={lockPayroll} disabled={busy || preview.rows.length === 0}>
              {pendingAction === "lock" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wallet className="h-4 w-4" />}
              Lock Payroll
            </Button>
          </div>
        </div>
      )}

      {error && (
        <p className="mt-4 rounded-xl border border-[color-mix(in_oklab,var(--danger)_35%,transparent)] bg-[color-mix(in_oklab,var(--danger)_10%,transparent)] px-3.5 py-2.5 text-xs font-semibold text-danger">
          {error}
        </p>
      )}

      <div className="mt-6 flex justify-end gap-2.5">
        <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
        {!preview && (
          <Button variant="accent" onClick={submit} disabled={busy || !startDate || !endDate || activeEmployees === 0}>
            {pendingAction === "generate" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wallet className="h-4 w-4" />}
            Generate Payroll
          </Button>
        )}
      </div>

      <AbsenceCalendarModal
        open={!!calendarModal}
        row={calendarModal?.row || null}
        preview={preview}
        initialViewMode={calendarModal?.initialViewMode || "attendance"}
        onClose={() => setCalendarModal(null)}
        onSave={saveCalendarOverrides}
      />
      <SalaryAdjustmentModal
        open={!!salaryAdjustmentModal}
        row={salaryAdjustmentModal?.row || null}
        onClose={() => setSalaryAdjustmentModal(null)}
        onSave={saveSalaryAdjustments}
      />
      <LoanDetailsModal
        open={!!loanDetailsModal}
        row={loanDetailsRow}
        onClose={() => setLoanDetailsModal(null)}
        pendingSkippedLoanIds={
          loanDetailsModal?.employeeId != null
            ? (loanSkips[String(loanDetailsModal.employeeId)] || [])
            : []
        }
        onQueueSkipCurrentPeriod={saveLoanSkips}
      />
    </Modal>
  );
}

function extractAttendanceOverrides(preview: PayrollPreview | null): AttendanceOverrideMap {
  const rows = preview?.rows || [];
  return rows.reduce<AttendanceOverrideMap>((acc, row) => {
    const overrides = row?.record?.attendanceOverrides || {};
    if (overrides && Object.keys(overrides).length) acc[String(row.employee.id)] = overrides;
    return acc;
  }, {});
}

function extractSalaryAdjustments(preview: PayrollPreview | null): SalaryAdjustmentMap {
  const rows = preview?.rows || [];
  return rows.reduce<SalaryAdjustmentMap>((acc, row) => {
    const adjustments = Array.isArray(row?.record?.salaryAdjustments) ? row.record.salaryAdjustments : [];
    if (adjustments.length) {
      acc[String(row.employee.id)] = adjustments.map((entry: any) => ({
        title: String(entry?.title || entry?.salaryAdjustment || "").trim(),
        value: String(entry?.value ?? entry?.daily ?? entry?.hourly ?? entry?.minute ?? entry?.amount ?? ""),
        unit: (String(entry?.unit || "amount").toLowerCase() as SalaryAdjustmentUnit),
      }));
    }
    return acc;
  }, {});
}

function extractLoanSkips(preview: PayrollPreview | null): LoanSkipMap {
  const rows = preview?.rows || [];
  return rows.reduce<LoanSkipMap>((acc, row) => {
    const skippedLoanIds = Array.isArray(row?.record?.loanSkippedIds)
      ? row.record.loanSkippedIds
          .map((value: unknown) => Number(value))
          .filter((value: number) => Number.isFinite(value) && value > 0)
      : [];
    if (skippedLoanIds.length) acc[String(row.employee.id)] = skippedLoanIds;
    return acc;
  }, {});
}

/* -------------------------------- list client -------------------------------- */
export function PayrollClient({
  periods,
  holidayCounts,
  activeEmployees,
  totals,
  presets,
  brand,
  initialFilters,
}: {
  periods: PeriodRow[];
  holidayCounts: Record<number, number>;
  activeEmployees: number;
  totals: { runs: number; net: string; gross: string };
  presets: PresetShape[];
  brand: PayrollBrand;
  initialFilters: { q: string; status: string };
}) {
  const quickRanges = presets.length ? presets : DEFAULT_PRESETS;
  const { values, set, reset } = useUrlFilters(initialFilters);
  const [modalOpen, setModalOpen] = useState(false);
  const hasFilters = values.q || values.status;

  return (
    <>
      <PageHeader
        eyebrow={brand.eyebrow}
        title={brand.title}
        description={brand.description}
        actions={
          <Button variant="accent" onClick={() => setModalOpen(true)} disabled={activeEmployees === 0}>
            <Plus className="h-4 w-4" /> Generate payroll
          </Button>
        }
      />

      <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
        {[
          { label: "Finalized runs", value: String(totals.runs), sub: "Locked & PIN-secured records" },
          { label: "Total net disbursed", value: formatPeso(Number(totals.net)), sub: "Take-home pay released" },
          { label: "Total gross computed", value: formatPeso(Number(totals.gross)), sub: "Before statutory deductions" },
        ].map((s) => (
          <Card key={s.label} className="rise-in p-5">
            <p className="text-[11px] font-bold tracking-[0.14em] text-faint uppercase">{s.label}</p>
            <RollingNumber text={s.value} className="font-display mt-2 text-2xl font-bold tracking-tight text-text" />
            <p className="mt-1.5 text-xs font-medium text-muted">{s.sub}</p>
          </Card>
        ))}
      </div>

      {activeEmployees === 0 && (
        <Card className="rise-in mb-4 border-[color-mix(in_oklab,var(--accent)_40%,transparent)] bg-[color-mix(in_oklab,var(--accent)_7%,transparent)] p-4">
          <p className="text-sm font-semibold text-text">No active employees yet.</p>
          <p className="mt-0.5 text-xs text-muted">
            <Link href="/employees" className="font-bold text-primary-strong underline underline-offset-2">Enroll employees</Link>{" "}
            before generating your first payroll run.
          </p>
        </Card>
      )}

      <Card className="rise-in mb-4 p-3.5">
        <div className="grid grid-cols-2 gap-2.5 md:grid-cols-[1fr_180px_auto]">
          <div className="relative col-span-2 md:col-span-1">
            <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-faint" />
            <TextInput
              className="pl-9"
              placeholder="Search period name\u2026"
              value={values.q}
              onChange={(e) => set("q", e.target.value)}
            />
          </div>
          <Select value={values.status} onChange={(e) => set("status", e.target.value)}>
            <option value="">All statuses</option>
            <option value="finalized">Finalized</option>
            <option value="voided">Voided</option>
          </Select>
          {hasFilters ? (
            <Button variant="ghost" onClick={reset}>
              <X className="h-4 w-4" /> Clear
            </Button>
          ) : (
            <div className="hidden items-center px-2 text-[11px] font-semibold text-faint md:flex">{periods.length} shown</div>
          )}
        </div>
      </Card>

      <Card className="rise-in overflow-hidden">
        {periods.length === 0 ? (
          <EmptyState
            icon={<FileSpreadsheet className="h-6 w-6" />}
            title={hasFilters ? "No matching periods" : "No payroll periods yet"}
            description={
              hasFilters
                ? "Try a different search or clear the filters."
                : "Generate your first payroll run \u2014 records here become locked, auditable, and PIN-protected."
            }
            action={
              !hasFilters && activeEmployees > 0 ? (
                <Button variant="accent" onClick={() => setModalOpen(true)}>
                  <Plus className="h-4 w-4" /> Generate payroll
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-sm">
              <thead>
                <tr className="border-b border-border bg-surface2 text-left text-[10px] font-bold tracking-[0.12em] text-faint uppercase">
                  <th className="px-5 py-3 sm:px-6">Period</th>
                  <th className="px-4 py-3">Coverage</th>
                  <th className="px-4 py-3 text-center">Holidays</th>
                  <th className="px-4 py-3 text-right">Employees</th>
                  <th className="px-4 py-3 text-right">Gross</th>
                  <th className="px-4 py-3 text-right">Deductions</th>
                  <th className="px-4 py-3 text-right">Net pay</th>
                  <th className="px-4 py-3 text-right">Status</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {periods.map((p) => (
                  <tr
                    key={p.id}
                    className={cn(
                      "group border-b border-border/60 transition-colors last:border-0 hover:bg-surface2/70",
                      p.status === "voided" && "opacity-55"
                    )}
                  >
                    <td className="px-5 py-3.5 sm:px-6">
                      <Link href={`${brand.basePath}/${p.id}`} className="font-semibold text-text group-hover:text-primary-strong">
                        {p.name}
                      </Link>
                      <p className="mt-0.5 text-[11px] text-faint">{p.workingDays} working days</p>
                    </td>
                    <td className="px-4 py-3.5 text-xs font-medium whitespace-nowrap text-muted">
                      {formatDate(p.startDate)} &mdash; {formatDate(p.endDate)}
                    </td>
                    <td className="px-4 py-3.5 text-center text-xs font-bold text-muted">
                      {holidayCounts[p.id] ?? 0}
                    </td>
                    <td className="px-4 py-3.5 text-right font-semibold text-muted">{p.employeeCount}</td>
                    <td className="px-4 py-3.5 text-right font-semibold whitespace-nowrap text-muted">
                      {p.status === "voided" ? "\u2014" : formatPeso(Number(p.totalGross), { decimals: 0 })}
                    </td>
                    <td className="px-4 py-3.5 text-right font-semibold whitespace-nowrap text-danger">
                      {p.status === "voided" ? "\u2014" : `\u2212${formatPeso(Number(p.totalDeductions), { decimals: 0 })}`}
                    </td>
                    <td className="px-4 py-3.5 text-right font-display font-bold whitespace-nowrap text-accent">
                      {p.status === "voided" ? "\u2014" : formatPeso(Number(p.totalNet))}
                    </td>
                    <td className="px-4 py-3.5 text-right"><StatusPill status={p.status} /></td>
                    <td className="px-4 py-3.5">
                      <Link
                        href={`${brand.basePath}/${p.id}`}
                        className="flex h-8 w-8 items-center justify-center rounded-lg text-faint transition-colors hover:bg-surface3 hover:text-text"
                        title="Open secured record"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {modalOpen && (
        <GenerateModal
          activeEmployees={activeEmployees}
          presets={quickRanges}
          basePath={brand.basePath}
          onClose={() => setModalOpen(false)}
        />
      )}
    </>
  );
}
